import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import {
  isMediaAuthorityCommandsFeatureEnabled,
  isMediaAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";
import {
  storageAuthorityService,
} from "@/services/storageAuthorityService";
import { rememberAuthorityAssetUrl } from "@/services/authorityAssetUrlRegistry";

const SCHEMA_VERSION = 1;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const MEDIA_ID_PATTERN = /^media_[A-Za-z0-9_-]{16,128}$/;
const MEDIA_TYPES = new Set([
  "image",
  "video",
  "audio",
  "documents",
  "embed",
  "poster",
  "tweet",
  "post",
  "text",
]);

const MEDIA_KEYS = Object.freeze([
  "organisation",
  "mediaType",
  "itemName",
  "caption",
  "description",
  "imageCaptions",
  "images",
  "mediaTypes",
  "mediaThumbnails",
  "mediaVariants",
  "mediaThumbnailVariants",
  "originalMediaThumbnails",
  "mediaUrl",
  "audioUrl",
  "videoUrl",
  "processedVideoUrl",
  "originalVideoUrl",
  "originalVideoUrls",
  "documentUrls",
  "documentMetadata",
  "variants",
  "thumbnailIndex",
  "embedHtml",
  "embedUrl",
  "embedProvider",
  "embedIsVertical",
  "embedIsSquare",
  "thumbnailUrl",
  "videoDeliveryProfile",
  "videoEncoderRevision",
  "assetIds",
]);
const MEDIA_CARD_KEYS = Object.freeze([
  "id",
  "type",
  "contentKind",
  "mediaType",
  "itemName",
  "title",
  "itemDescription",
  "description",
  "caption",
  "imageCaptions",
  "organisation",
  "owner",
  "images",
  "mediaTypes",
  "mediaThumbnails",
  "mediaUrl",
  "audioUrl",
  "documentUrls",
  "documentMetadata",
  "thumbnailIndex",
  "embedHtml",
  "embedUrl",
  "embedProvider",
  "thumbnailUrl",
  "videoDeliveryProfile",
  "videoEncoderRevision",
  "createdAt",
  "_authorityMedia",
  "_legacyMediaCompatibility",
  "assetIds",
]);

export class MediaAuthorityServiceError extends Error {
  constructor(code = "unknown") {
    const messages = {
      "feature-disabled": "Media publishing is currently unavailable.",
      unauthenticated: "Sign in before publishing media.",
      "invalid-argument": "The media post is invalid.",
      "permission-denied": "You cannot publish media to this organisation.",
      "not-found": "The media post was not found.",
      aborted: "The media post changed. Retry the operation.",
      unavailable: "Media publishing is currently unavailable.",
      unknown: "The media post could not be saved.",
    };
    super(messages[code] || messages.unknown);
    this.name = "MediaAuthorityServiceError";
    this.code = messages[code] ? code : "unknown";
  }
}

function fail(code) {
  throw new MediaAuthorityServiceError(code);
}

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value, expected) {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value).sort();
  return keys.length === expected.length && keys.every((key, index) => key === [...expected].sort()[index]);
}

function operationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) fail("invalid-argument");
  return value;
}

function mediaId(value) {
  if (typeof value !== "string" || !MEDIA_ID_PATTERN.test(value)) fail("invalid-argument");
  return value;
}

function arrayValue(value) {
  return Array.isArray(value) ? [...value] : [];
}

function mediaPayload(value) {
  const arrays = new Set([
    "images",
    "imageCaptions",
    "mediaTypes",
    "mediaThumbnails",
    "originalMediaThumbnails",
    "originalVideoUrls",
    "documentUrls",
    "documentMetadata",
  ]);
  const objects = new Set(["variants", "mediaVariants", "mediaThumbnailVariants"]);
  const normalized = Object.fromEntries(MEDIA_KEYS.map((key) => {
    if (value?.[key] !== undefined) return [key, value[key]];
    if (key === "embedProvider") return [key, null];
    if (key === "thumbnailIndex") return [key, 0];
    if (key === "mediaType") return [key, "image"];
    if (arrays.has(key)) return [key, []];
    if (objects.has(key)) return [key, {}];
    if (key === "assetIds") return [key, {}];
    if (key.startsWith("embedIs")) return [key, false];
    return [key, ""];
  }));
  if (!MEDIA_TYPES.has(normalized.mediaType) || typeof normalized.organisation !== "string" || !normalized.organisation.trim()) fail("invalid-argument");
  return Object.freeze(normalized);
}

async function resolveAuthorityAssets(media) {
  const bindings = isRecord(media?.assetIds) ? media.assetIds : {};
  const resolved = { ...media, assetIds: { ...bindings } };
  await Promise.all(Object.entries(bindings).map(async ([field, entry]) => {
    const ids = Array.isArray(entry) ? entry : [entry];
    const urls = await Promise.all(ids.map(async (assetId) => {
      const url = (await storageAuthorityService.createAssetDownloadUrl(assetId)).downloadUrl;
      rememberAuthorityAssetUrl(url, assetId);
      return url;
    }));
    resolved[field] = Array.isArray(entry) ? urls : urls[0];
  }));
  return Object.freeze(resolved);
}

function requestForCreate(payload, operation) {
  return Object.freeze({ schemaVersion: SCHEMA_VERSION, operationId: operationId(operation), media: mediaPayload(payload) });
}

function requestForUpdate(id, payload, operation, expectedRevision) {
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 1) fail("invalid-argument");
  return Object.freeze({ schemaVersion: SCHEMA_VERSION, operationId: operationId(operation), expectedRevision, mediaId: mediaId(id), media: mediaPayload(payload) });
}

function requestForDelete(id, operation, expectedRevision) {
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 1) fail("invalid-argument");
  return Object.freeze({ schemaVersion: SCHEMA_VERSION, operationId: operationId(operation), expectedRevision, mediaId: mediaId(id) });
}

function normalizeResponse(value, expectedId = null) {
  if (!isRecord(value) || value.schemaVersion !== SCHEMA_VERSION || typeof value.id !== "string" || !["created", "updated", "deleted", "replayed"].includes(value.outcome) || value.state !== "completed") fail("invalid-response");
  if (expectedId && value.id !== expectedId) fail("invalid-response");
  return Object.freeze({ id: value.id, outcome: value.outcome, state: value.state, revision: Number.isSafeInteger(value.revision) ? value.revision : null });
}

function normalizeReadResponse(value, expectedId) {
  if (!isRecord(value) || value.schemaVersion !== SCHEMA_VERSION || value.id !== expectedId || !isRecord(value.media) || !Number.isSafeInteger(value.revision)) {
    fail("invalid-response");
  }
  return Object.freeze({ id: value.id, revision: value.revision, ...value.media });
}

function normalizeMediaCard(value) {
  if (!isRecord(value) || !exactKeys(value, MEDIA_CARD_KEYS)) fail("invalid-response");
  if (
    typeof value.id !== "string" ||
    value.type !== "media" ||
    value.contentKind !== "mediaPost" ||
    !MEDIA_TYPES.has(value.mediaType) ||
    typeof value.itemName !== "string" ||
    typeof value.title !== "string" ||
    typeof value.itemDescription !== "string" ||
    typeof value.description !== "string" ||
    typeof value.caption !== "string" ||
    typeof value.organisation !== "string" ||
    typeof value.owner !== "string" ||
    !Array.isArray(value.imageCaptions) ||
    !Array.isArray(value.images) ||
    !Array.isArray(value.mediaTypes) ||
    !Array.isArray(value.mediaThumbnails) ||
    !Array.isArray(value.documentUrls) ||
    !Array.isArray(value.documentMetadata) ||
    !Number.isSafeInteger(value.thumbnailIndex) ||
    typeof value.embedHtml !== "string" ||
    typeof value.embedUrl !== "string" ||
    (value.embedProvider !== null && typeof value.embedProvider !== "string") ||
    typeof value.thumbnailUrl !== "string" ||
    typeof value.videoDeliveryProfile !== "string" ||
    typeof value.videoEncoderRevision !== "string" ||
    !isRecord(value.assetIds) ||
    (value.createdAt !== null && !Number.isSafeInteger(value.createdAt)) ||
    typeof value._authorityMedia !== "boolean" ||
    typeof value._legacyMediaCompatibility !== "boolean"
  ) {
    fail("invalid-response");
  }
  if (value._authorityMedia === value._legacyMediaCompatibility) fail("invalid-response");
  return Object.freeze(value);
}

function normalizeListResponse(value, request) {
  if (!isRecord(value) || value.schemaVersion !== SCHEMA_VERSION || value.scope !== request.scope || value.orgId !== request.orgId || !Array.isArray(value.items) || typeof value.hasMore !== "boolean" || (value.nextCursor !== null && typeof value.nextCursor !== "string")) {
    fail("invalid-response");
  }
  if (value.items.length > request.limit || value.hasMore !== (value.nextCursor !== null)) fail("invalid-response");
  const items = value.items.map(normalizeMediaCard);
  if (new Set(items.map((item) => item.id)).size !== items.length) fail("invalid-response");
  return Object.freeze({ items: Object.freeze(items), hasMore: value.hasMore, nextCursor: value.nextCursor });
}

function normalizeError(error) {
  if (error instanceof MediaAuthorityServiceError) return error;
  const raw = typeof error?.code === "string" ? error.code : "unknown";
  const code = raw.includes("/") ? raw.slice(raw.lastIndexOf("/") + 1) : raw;
  return new MediaAuthorityServiceError(code);
}

export function createMediaAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  readsEnabled = isMediaAuthorityReadsFeatureEnabled,
  commandsEnabled = isMediaAuthorityCommandsFeatureEnabled,
} = {}) {
  const callables = new Map();
  const callableFor = (name) => {
    if (!callables.has(name)) callables.set(name, callableFactory(functionsInstance, name));
    return callables.get(name);
  };
  const invoke = async (name, request, command) => {
    if (command ? commandsEnabled !== true : readsEnabled !== true) fail("feature-disabled");
    const actor = authInstance?.currentUser ?? null;
    if (command && !actor?.uid) fail("unauthenticated");
    try {
      const response = await withUiDeadline(callableFor(name)(request), { phase: "saving media", timeoutMs: 30_000 });
      if ((authInstance?.currentUser ?? null) !== actor) fail("stale-auth-context");
      return response?.data;
    } catch (error) {
      throw normalizeError(error);
    }
  };
  return Object.freeze({
    async createMediaPost(payload, operation) {
      return normalizeResponse(await invoke("createMediaPost", requestForCreate(payload, operation), true));
    },
    async updateMediaPost(id, payload, operation, expectedRevision) {
      return normalizeResponse(await invoke("updateMediaPost", requestForUpdate(id, payload, operation, expectedRevision), true), id);
    },
    async deleteMediaPost(id, operation, expectedRevision) {
      return normalizeResponse(await invoke("deleteMediaPost", requestForDelete(id, operation, expectedRevision), true), id);
    },
    async getMediaPost(id) {
      const value = await invoke("getMediaPost", { schemaVersion: SCHEMA_VERSION, mediaId: mediaId(id) }, false);
      return resolveAuthorityAssets(normalizeReadResponse(value, id));
    },
    async listMediaPosts({ scope = "open", orgId = null, limit = 12 } = {}) {
      if (!["open", "organisation-public", "organisations"].includes(scope) || !Number.isSafeInteger(limit) || limit < 1 || limit > 25) fail("invalid-argument");
      const request = Object.freeze({ schemaVersion: SCHEMA_VERSION, scope, orgId, limit });
      const result = normalizeListResponse(await invoke("listMediaPosts", request, false), request);
      const items = await Promise.all(result.items.map(resolveAuthorityAssets));
      return Object.freeze({ ...result, items: Object.freeze(items) });
    },
  });
}

export const mediaAuthorityService = createMediaAuthorityService();

export { exactKeys, mediaPayload, requestForCreate, requestForDelete, requestForUpdate };
